import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { useNewInAppChatMessage } from "@/hooks/use-boot-in-app-chat";
import { useGetOrCreateDomainVerification, useVerifyDomain } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { isValidDomain } from "@/utils/url-validation";
import { DomainVerificationState } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useId, useRef, useState, type FC, type FormEvent } from "react";

type DomainVerificationProps = {
    organizationId: string;
    /** If empty, shows an input for the user to enter the domain first */
    domain?: string;
    onCancel: () => void;
    /** Called when a domain verification is successfully created */
    onDomainCreated?: (domain: string) => void;
};

export const DomainVerification: FC<DomainVerificationProps> = ({
    onCancel,
    domain: initialDomain = "",
    onDomainCreated,
}) => {
    const { toast } = useToast();
    const showNewInAppChatMessage = useNewInAppChatMessage();

    // State for domain input when no domain is provided
    const [domainInput, setDomainInput] = useState(initialDomain);
    const [domainInputError, setDomainInputError] = useState("");
    const [confirmedDomain, setConfirmedDomain] = useState(initialDomain);

    // Only fetch/create verification when we have a confirmed domain
    const { domainVerification, error } = useGetOrCreateDomainVerification(confirmedDomain || undefined);
    const token = domainVerification?.verificationToken ?? "";

    // Call onDomainCreated when a new domain verification is successfully created
    const hasNotifiedDomainCreated = useRef(false);

    // Reset notification flag when domain changes to handle component reuse
    useEffect(() => {
        hasNotifiedDomainCreated.current = false;
    }, [confirmedDomain]);

    useEffect(() => {
        if (domainVerification && confirmedDomain && onDomainCreated && !hasNotifiedDomainCreated.current) {
            hasNotifiedDomainCreated.current = true;
            onDomainCreated(confirmedDomain);
        }
    }, [domainVerification, confirmedDomain, onDomainCreated]);

    const verifyDomain = useVerifyDomain();
    const handleVerifyDomain = useCallback(async () => {
        if (!domainVerification) {
            return;
        }
        try {
            const result = await verifyDomain.mutateAsync(domainVerification);
            const title =
                result?.state === DomainVerificationState.VERIFIED
                    ? `${confirmedDomain} is verified.`
                    : `${confirmedDomain} is not yet verified. Please try again later.`;

            toast({
                title,
            });
            onCancel();
        } catch (error) {
            toast({
                title: "Failed to verify domain.",
                description: formatError(error),
            });
        }
    }, [confirmedDomain, domainVerification, toast, verifyDomain, onCancel]);

    // Handle domain input submission
    const handleSubmitDomain = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmedDomain = domainInput.trim().toLowerCase();

            if (!trimmedDomain) {
                setDomainInputError("Domain is required.");
                return;
            }

            if (!isValidDomain(trimmedDomain)) {
                setDomainInputError("Please enter a valid domain (e.g., example.com).");
                return;
            }

            setDomainInputError("");
            setConfirmedDomain(trimmedDomain);
        },
        [domainInput],
    );

    const handleDomainInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setDomainInput(e.target.value);
        setDomainInputError("");
    }, []);

    // ids for the input fields
    const inputDomainID = useId();
    const inputTokenKeyID = useId();
    const inputTokenValueID = useId();

    const handleReachOut = useCallback(() => {
        showNewInAppChatMessage(
            `Hi! I need to verify my "${confirmedDomain}" domain for SSO but cannot create any DNS TXT record. Can you help me?`,
        );
    }, [showNewInAppChatMessage, confirmedDomain]);

    // Step 1: Domain input (when no domain provided)
    if (!confirmedDomain) {
        return (
            <div>
                <DialogHeader className="flex flex-col gap-2">
                    <DialogTitle>Add email domain</DialogTitle>
                    <DialogDescription>
                        Enter the email domain you want to add for SSO authentication.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex w-full py-6">
                    <form id="add-domain-form" onSubmit={handleSubmitDomain} className="flex w-full flex-col gap-4">
                        <Text>
                            After adding the domain, you&apos;ll need to verify ownership by adding a DNS TXT record.
                        </Text>
                        <InputField
                            label="Email domain"
                            id={inputDomainID}
                            hint="This is the work email domain that people will use to sign in."
                            error={domainInputError}
                        >
                            <Input
                                data-testid="new-domain-input"
                                id={inputDomainID}
                                type="text"
                                name="domain"
                                placeholder="example.com"
                                value={domainInput}
                                onChange={handleDomainInputChange}
                                autoFocus
                            />
                        </InputField>
                    </form>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" onClick={onCancel} data-tracking-id="cancel-add-domain">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="add-domain-form"
                        variant="primary"
                        disabled={!domainInput.trim()}
                        data-tracking-id="continue-add-domain"
                    >
                        Continue
                    </Button>
                </DialogFooter>
            </div>
        );
    }

    // Step 2: Domain verification
    return (
        <div>
            <DialogHeader className="flex flex-col gap-2">
                <DialogTitle>Verify your domain</DialogTitle>
                <DialogDescription>
                    Configure OpenID Connect (OIDC) single sign-on with your identity provider.
                </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex w-full py-6">
                <div className="flex w-full flex-col gap-4">
                    <Text>
                        We need to ensure you own the domain. To do that, you&apos;ll need to create a new DNS TXT
                        record for your domain.{" "}
                        <ExternalLink href="https://ona.com/docs/ona/sso/overview">Learn more</ExternalLink>.
                    </Text>
                    {error && (
                        <Banner
                            variant="danger"
                            text={
                                <>
                                    <div className="font-bold">Failed to fetch the verification data.</div>
                                    <div className="text-sm">{formatError(error)}</div>
                                </>
                            }
                        />
                    )}
                    <InputField
                        label="Email domain"
                        id={inputDomainID}
                        hint="This is the work email domain that people will use to sign in."
                    >
                        <Input
                            data-testid="domain-input"
                            id={inputDomainID}
                            type="text"
                            name="domain"
                            value={confirmedDomain}
                            disabled
                        />
                    </InputField>
                    <InputField
                        label="TXT record name"
                        id={inputTokenKeyID}
                        hint={"This is the name of the TXT record."}
                    >
                        <CopyableInput
                            disabled={!token}
                            data-testid="token-name-input"
                            data-tracking-id="txt-record-name-domain-verification"
                            id={inputTokenKeyID}
                            type="text"
                            name="token-name"
                            value={confirmedDomain}
                            readOnly={true}
                        />
                    </InputField>
                    <InputField
                        label="TXT record value"
                        id={inputTokenValueID}
                        hint={"This is the value of the TXT record."}
                    >
                        <CopyableInput
                            disabled={!token}
                            data-testid="token-value-input"
                            data-tracking-id="txt-record-value-domain-verification"
                            id={inputTokenValueID}
                            type="text"
                            name="token-value"
                            value={token}
                            readOnly={true}
                        />
                    </InputField>
                    <Text>
                        If you cannot use TXT record validation,{" "}
                        <Button
                            variant="link"
                            className="p-0 text-base text-content-link"
                            onClick={handleReachOut}
                            data-tracking-id="reach-out-to-us-domain-verification"
                        >
                            please reach out to us.
                        </Button>
                    </Text>
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" onClick={onCancel} data-tracking-id="cancel-domain-verification">
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    autoFocus={true}
                    variant="primary"
                    disabled={!domainVerification}
                    onClick={handleVerifyDomain}
                    data-tracking-id="verify-domain-verification"
                >
                    Verify
                </Button>
            </DialogFooter>
        </div>
    );
};
